import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, ExternalLink, Megaphone, Link2, HeadphonesIcon, FileText, ShieldCheck, KeyRound } from 'lucide-react';
import CryptoJS from 'crypto-js';

import { useAuth } from '../contexts/AuthContext';
import ChangePasswordModal from '../components/ChangePasswordModal';
import FullscreenLoader from './FullscreenLoader';
import CihLogin from './CihLogin';
import LoginPopup from '../components/LoginPopup';
import api from '../services/api';
import { getAdminHomePath, isCihHost } from '../config/apiBase';

const SECRET_KEY = 'horapilot-secret-key-2025';
const encryptPassword = (password) => CryptoJS.AES.encrypt(password, SECRET_KEY).toString();
const decryptPassword = (encryptedPassword) => {
  try { const bytes = CryptoJS.AES.decrypt(encryptedPassword, SECRET_KEY); return bytes.toString(CryptoJS.enc.Utf8); }
  catch { return ''; }
};

// ============================================================================
// 🔒 VALEURS PAR DÉFAUT (fallback si API indisponible + premier chargement)
// ============================================================================
const DEFAULT_CONFIG = {
  background_image: { url: '/images/backgroundlogin.jpg', overlay_opacity: 0.4 },
  hero_texts: [
    { lines: ['Votre', 'Entreprise', 'Un seul outil'], color: '#fdfcf5', subtitle: 'Simplifiez, automatisez et optimisez tous vos processus avec une solution moderne et intuitive.' },
    { lines: ['Modulaire', 'Fiable', 'Evolutif'], color: '#fdfcf5', subtitle: 'La plateforme de gestion qui accompagne votre entreprise au quotidien.' },
    { lines: ['Simple', 'Rapide', 'Efficace'], color: '#fdfcf5', subtitle: 'Conçu pour les entrepreneurs africains ambitieux.' },
    { lines: ['Gérez', 'votre entreprise', 'efficacement'], color: '#fdfcf5', subtitle: 'HoraPilot centralise la gestion RH, comptabilité et opérations dans un seul outil.' }
  ],
  ticker_ads: [
    { company: 'MTN Business', tagline: 'Connectez votre entreprise au réseau le plus fiable', url: 'https://www.mtn.com' },
    { company: 'Ecobank', tagline: 'Ouvrez votre compte pro en 24h', url: 'https://www.ecobank.com' },
    { company: 'DHL Express', tagline: 'Livraison express partout en Afrique', url: 'https://www.dhl.com' },
    { company: 'Orange Business', tagline: 'Solutions digitales pour entreprises', url: 'https://www.orange.com' },
  ],
  featured_ads: [
    { id: 1, company: 'KLASYS Gestion scolaire', tagline: 'Connectez votre entreprise au réseau le plus fiable d\'Afrique. Internet haut débit pro.', cta: 'Découvrir', url: 'https://www.mtn.com' },
    { id: 2, company: 'Ecobank', tagline: 'Ouvrez votre compte pro en 24h. Services bancaires digitaux pour PME africaines.', cta: 'En savoir plus', url: 'https://www.ecobank.com' },
    { id: 3, company: 'DHL Express', tagline: 'Livraison express en Afrique et dans le monde. Expédiez vos colis en confiance.', cta: 'Expédier', url: 'https://www.dhl.com' }
  ],

 footer_links: [
  { label: 'www.danicedgroup.com', url: 'https://www.danicedgroup.com', external: true },
  { label: 'Aide et contact', url: '#aide', external: false },
  { label: "Conditions d'utilisation", url: '#cgu', external: false },
  { label: 'Politique de confidentialité', url: '#confidentialite', external: false }
]

};

// ============================================================================
// 📦 Helpers localStorage cache
// ============================================================================
const CACHE_KEY = 'hp_login_config';

const getCachedConfig = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch (e) { /* ignore */ }
  return null;
};

const setCachedConfig = (config) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(config));
  } catch (e) { /* ignore */ }
};

// ============================================================================
// 🖼️ Préchargement image
// ============================================================================

// preloadImage — utilisée uniquement avec le système dynamique d'image de fond
// Pour réactiver, décommenter et réactiver le bloc backgroundImage dynamique
/*
const preloadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        // Convertir en base64 et mettre en cache
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        localStorage.setItem('hp_bg_image_cache', base64);
        resolve(base64);
      } catch (e) {
        // Si canvas échoue (CORS), retourner l'URL directe
        resolve(url);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};
*/



const LoginRH = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, error: contextError, loading: contextLoading } = useAuth();

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpSetup, setTotpSetup] = useState(null);

  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isAdAnimating, setIsAdAnimating] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [loaderCompany, setLoaderCompany] = useState('');

  // ============================================================================
  // 📡 CONFIG DYNAMIQUE — localStorage cache → API → mise à jour silencieuse
  // ============================================================================
  const initialConfig = getCachedConfig() || DEFAULT_CONFIG;

  const [heroTexts, setHeroTexts] = useState(initialConfig.hero_texts || DEFAULT_CONFIG.hero_texts);
  const [tickerAds, setTickerAds] = useState(initialConfig.ticker_ads || DEFAULT_CONFIG.ticker_ads);
  const [featuredAds, setFeaturedAds] = useState(initialConfig.featured_ads || DEFAULT_CONFIG.featured_ads);
  const [footerLinks, setFooterLinks] = useState(initialConfig.footer_links || DEFAULT_CONFIG.footer_links);
  const [copyrightText, setCopyrightText] = useState('HoraPilot est une suite logicielle éditée par DANICED GROUP · Tous droits de reproduction interdits');
  const [popupAide, setPopupAide] = useState({ titre: 'Aide et contact', contenu: '' });
  const [popupCgu, setPopupCgu] = useState({ titre: "Conditions d'Utilisation", contenu: '' });
  const [popupConfidentialite, setPopupConfidentialite] = useState({ titre: 'Politique de Confidentialité', contenu: '' });
  const [activePopup, setActivePopup] = useState(null);



  // IMAGE DE FOND — Version statique pour une expérience optimale (chargement instantané)
  // Pour revenir au système dynamique via le centre d'administration,
  // décommenter le bloc ci-dessous et supprimer la ligne const [backgroundImage...]
  /*
  const [backgroundImage, setBackgroundImage] = useState(() => {
    try {
      const cachedB64 = localStorage.getItem('hp_bg_image_cache');
      if (cachedB64) return cachedB64;
      const cachedConfig = localStorage.getItem('hp_login_config');
      if (cachedConfig) {
        const parsed = JSON.parse(cachedConfig);
        if (parsed?.background_image?.url) return parsed.background_image.url;
      }
    } catch (e) {}
    return DEFAULT_CONFIG.background_image.url;
  });
  */
  const [backgroundImage] = useState(DEFAULT_CONFIG.background_image.url);
    

  // Charger la config depuis l'API en arrière-plan
useEffect(() => {
    // Retirer le fond CSS inline dès que React est prêt
    if (window.__removeLoginBg) window.__removeLoginBg();

    const loadConfig = async () => {
      try {
        const response = await fetch('/api/login-config');
        if (!response.ok) return;
        const result = await response.json();
        if (!result.success || !result.data) return;

        const data = result.data;

        if (data.hero_texts) setHeroTexts(data.hero_texts);
        if (data.ticker_ads) setTickerAds(data.ticker_ads);
        if (data.featured_ads) setFeaturedAds(data.featured_ads);
        if (data.footer_links) setFooterLinks(data.footer_links);

        if (data.copyright_text) setCopyrightText(data.copyright_text);
        if (data.popup_aide) setPopupAide(data.popup_aide);
        if (data.popup_cgu) setPopupCgu(data.popup_cgu);
        if (data.popup_confidentialite) setPopupConfidentialite(data.popup_confidentialite);

        setCachedConfig(data);
      } catch (e) {
        // API indisponible → on garde le cache/fallback
      }
    };

    loadConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  // Hero rotation
  useEffect(() => {
    if (heroTexts.length === 0) return;
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => { setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length); setIsAnimating(false); }, 500);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroTexts.length]);

  // Ad rotation
  useEffect(() => {
    if (featuredAds.length === 0) return;
    const interval = setInterval(() => {
      setIsAdAnimating(true);
      setTimeout(() => { setCurrentAdIndex((prev) => (prev + 1) % featuredAds.length); setIsAdAnimating(false); }, 400);
    }, 7000);
    return () => clearInterval(interval);
  }, [featuredAds.length]);

  const currentText = heroTexts.length > 0 ? heroTexts[currentTextIndex % heroTexts.length] : DEFAULT_CONFIG.hero_texts[0];
  const currentAd = featuredAds.length > 0 ? featuredAds[currentAdIndex % featuredAds.length] : null;

  useEffect(() => {
    const p = searchParams.get('error');
    if (p === 'company_blocked') setError('🚫 Votre compte entreprise est bloqué. Merci de régulariser le mois impayé, puis de contacter le support afin d\'être débloqué.');
    else if (p === 'company_suspended') setError('⏸️ Votre abonnement est suspendu. Veuillez renouveler votre abonnement.');
    else if (p === 'company_expired') setError('⏱️ Votre période d\'essai est expirée. Contactez le support pour continuer.');
  }, [searchParams]);

  useEffect(() => {
    const se = localStorage.getItem('rememberedEmail'), sp = localStorage.getItem('rememberedPassword'), sr = localStorage.getItem('rememberMe');
    if (sr === 'true' && se) { setFormData({ email: se, password: sp ? decryptPassword(sp) : '' }); setRememberMe(true); }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    if (totpRequired) {
      setTotpRequired(false);
      setTotpCode('');
      setTotpSetup(null);
    }
  };

  const handlePasswordChange = async (currentPassword, newPassword) => {
    try {
      const result = await api.auth.changePasswordFirstLogin(currentPassword, newPassword);
      if (result.success) {
        setShowPasswordModal(false); setError('');
        const userData = JSON.parse(localStorage.getItem('user'));
        if (userData) { userData.first_login = false; localStorage.setItem('user', JSON.stringify(userData)); if (userData.role === 'super_admin') navigate(getAdminHomePath()); else { localStorage.removeItem('horapilot_rights'); sessionStorage.removeItem('kioskMode'); sessionStorage.removeItem('pointage_viewMode'); navigate('/'); } }
      } else throw new Error(result.error || 'Erreur lors du changement de mot de passe');
    } catch (error) { throw error; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (totpRequired && !/^\d{6}$/.test(totpCode.trim())) {
        setError('Saisis le code Google Authenticator a 6 chiffres.');
        setLoading(false);
        return;
      }

      const result = await login(formData.email, formData.password, totpRequired ? totpCode.trim() : '');
      if (result.requiresTotp) {
        setTotpRequired(true);
        setTotpSetup(result.totpSetup || null);
        setError('');
        setLoading(false);
        return;
      }

      if (result.success && result.user) {
        setTotpRequired(false);
        setTotpCode('');
        setTotpSetup(null);
        if (rememberMe) { localStorage.setItem('rememberedEmail', formData.email); localStorage.setItem('rememberedPassword', encryptPassword(formData.password)); localStorage.setItem('rememberMe', 'true'); }
        else { localStorage.removeItem('rememberedEmail'); localStorage.removeItem('rememberedPassword'); localStorage.removeItem('rememberMe'); }
        if (result.user.first_login) { setTempPassword(formData.password); setShowPasswordModal(true); setLoading(false); return; }
        
        const companyName = result.user.company_name || 'HoraPilot';
        setLoaderCompany(companyName);
        setShowLoader(true);
        setLoading(false);
        
        setTimeout(() => {
          if (result.user.role === 'super_admin') navigate(getAdminHomePath());
          else { localStorage.removeItem('horapilot_rights'); sessionStorage.removeItem('kioskMode'); sessionStorage.removeItem('pointage_viewMode'); navigate('/'); }
        }, 1600);

        return;
      } else { setError(result.error || 'Erreur de connexion'); setLoading(false); }
    } catch (err) { setError(err.message || 'Erreur de connexion'); setLoading(false); }
  };

  const getErrorType = () => {
    const msg = error || contextError;
    if (!msg) return null;
    if (msg.includes('bloqué') || msg.includes('blocked')) return 'blocked';
    if (msg.includes('suspendu') || msg.includes('suspended')) return 'suspended';
    if (msg.includes('expiré') || msg.includes('expired')) return 'expired';
    return 'error';
  };

  const errorType = getErrorType();
  const displayError = error || contextError;

  const resetTotpFlow = () => {
    setTotpRequired(false);
    setTotpCode('');
    setTotpSetup(null);
    setError('');
  };

  if (isCihHost()) {
    return (
      <>
        <CihLogin
          formData={formData}
          handleChange={handleChange}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          displayError={displayError}
          errorType={errorType}
          handleSubmit={handleSubmit}
          loading={loading}
          contextLoading={contextLoading}
          totpRequired={totpRequired}
          totpSetup={totpSetup}
          totpCode={totpCode}
          setTotpCode={setTotpCode}
          setError={setError}
          resetTotpFlow={resetTotpFlow}
        />

        {showPasswordModal && (
          <ChangePasswordModal onClose={() => setShowPasswordModal(false)} onSubmit={handlePasswordChange} tempPassword={tempPassword} />
        )}

        <FullscreenLoader
          visible={showLoader}
          duration={1800}
          message={`Bienvenue dans votre espace ${loaderCompany}`}
        />
      </>
    );
  }

  const tickerItems = [...tickerAds, ...tickerAds];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');

        .login-page * { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; }

        /* ══════ TOP TICKER ══════ */
        .ad-ticker {
          position: fixed; top: 0; left: 0; right: 0; z-index: 50;
          height: 44px;
          background: rgba(68,134,150,0.92);
          backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
          border-bottom: 1px solid rgba(253,252,245,0.1);
          display: flex; align-items: center; overflow: hidden;
        }
        .ad-ticker-label {
          flex-shrink: 0; padding: 0 20px;
          font-size: 9px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 1.5px; color: #fdfcf5;
          display: flex; align-items: center; gap: 6px;
          border-right: 1px solid rgba(253,252,245,0.15); height: 100%;
        }
        .ad-ticker-track { flex: 1; overflow: hidden; position: relative; height: 100%; display: flex; align-items: center; }
        .ad-ticker-scroll {
          display: flex; align-items: center;
          animation: tickerScroll 30s linear infinite; white-space: nowrap;
        }
        .ad-ticker-item {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 0 32px; font-size: 13px; color: rgba(253,252,245,0.75);
          text-decoration: none; transition: color 0.2s; flex-shrink: 0;
        }
        .ad-ticker-item:hover { color: #fed774; }
        .ad-ticker-item .ticker-company { font-weight: 700; color: #fdfcf5; }
        .ad-ticker-item .ticker-sep { color: rgba(253,252,245,0.3); margin: 0 4px; }
        .ad-ticker-item .ticker-dot { width: 4px; height: 4px; border-radius: 50%; background: rgba(253,252,245,0.25); flex-shrink: 0; }
        @keyframes tickerScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }

        /* ══════ MAIN ══════ */
        .login-page-main { min-height: 100vh; display: flex; position: relative; overflow: hidden; padding-top: 44px; background: #1a1a1a; }

        
       .login-bg { position: fixed; inset: 0; z-index: 0; }
        .login-bg img { width: 100%; height: 100%; object-fit: cover; }


        .login-bg::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(to right, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 45%, rgba(0,0,0,0.15) 100%);
        }

        .login-branding {
          position: relative; z-index: 1; flex: 1.1;
          display: flex; flex-direction: column; justify-content: space-between;
          padding: 40px 48px 100px; color: #fdfcf5; min-height: calc(100vh - 44px);
        }

        /* ─── Featured Ad Card ─── */
        .ad-featured { width: 100%; max-width: 440px; }
        .ad-card {
          border-radius: 16px; overflow: hidden;
          background: rgba(253,252,245,0.06);
          backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(253,252,245,0.1); transition: all 0.3s ease;
          text-decoration: none; display: block; color: inherit;
        }
        .ad-card:hover {
          background: rgba(253,252,245,0.1); border-color: rgba(253,252,245,0.18);
          transform: translateY(-2px); box-shadow: 0 12px 40px rgba(0,0,0,0.25);
        }
        
        .ad-card-image-badge {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 4px 12px; border-radius: 20px; margin: 16px 0 0 20px;
          background: rgba(253,252,245,0.1); border: 1px solid rgba(253,252,245,0.15);
          font-size: 9px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 1px; color: #fdfcf5;
        }

        .ad-card-body { padding: 18px 20px; }
        .ad-card-header { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
        .ad-card-company { font-size: 15px; font-weight: 700; color: #fdfcf5; }
        .ad-card-tagline { font-size: 13.5px; font-weight: 500; color: rgba(253,252,245,0.8); line-height: 1.45; margin-bottom: 14px; }
        .ad-card-footer { display: flex; align-items: center; justify-content: space-between; }
        .ad-card-cta {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 6px 14px; border-radius: 8px;
          background: rgba(254,215,116,0.15); border: 1px solid rgba(254,215,116,0.25);
          font-size: 12px; font-weight: 700; color: #fed774;
          text-decoration: none; transition: all 0.2s;
        }
        .ad-card-cta:hover { background: rgba(254,215,116,0.25); gap: 10px; }
        .ad-card-dots { display: flex; gap: 4px; }
        .ad-card-dot { width: 6px; height: 6px; border-radius: 50%; transition: all 0.3s; }
        .ad-card-dot.active { background: #fed774; transform: scale(1.2); }
        .ad-card-dot.inactive { background: rgba(253,252,245,0.2); }
        .ad-card-animated { transition: all 0.4s ease-in-out; }
        .ad-card-animated.out { opacity: 0; transform: translateY(6px); }
        .ad-card-animated.in { opacity: 1; transform: translateY(0); }

        /* ─── Brand ─── */
        .brand-section { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; }
        .brand-logo { display: flex; align-items: center; gap: 20px; margin-bottom: 44px; }
        .brand-logo img {
          width: 80px; height: 80px; object-fit: contain;
          filter: brightness(0) saturate(100%) invert(87%) sepia(30%) saturate(700%) hue-rotate(5deg) brightness(105%) contrast(101%);
        }
        .brand-logo-text {
          font-size: 44px; font-weight: 700; letter-spacing: 3px; color: #fed774;
          text-shadow: 0 2px 16px rgba(0,0,0,0.35);
        }

        .hero-container { position: relative; min-height: 220px; max-width: 560px; }
        .hero-animated { position: absolute; top: 0; left: 0; right: 0; transition: all 0.5s ease-in-out; }
        .hero-animated.fade-out { opacity: 0; transform: translateY(-12px); }
        .hero-animated.fade-in { opacity: 1; transform: translateY(0); }
        .hero-headline { font-size: 44px; font-weight: 700; line-height: 1.15; margin-bottom: 16px; }
        .hero-subtitle { font-size: 17px; line-height: 1.6; color: rgba(253,252,245,0.82); font-weight: 400; max-width: 480px; }
        .hero-dots { display: flex; gap: 6px; margin-top: 8px; }
        .hero-dot { height: 4px; border-radius: 4px; transition: all 0.4s ease; }
        .hero-dot.active { width: 36px; background: #fed774; }
        .hero-dot.inactive { width: 10px; background: rgba(253,252,245,0.3); }

        /* ─── Card ─── */
        .login-card-wrapper {
          position: relative; z-index: 2;
          display: flex; align-items: center; justify-content: center;
          padding: 40px 48px 100px 32px; min-height: calc(100vh - 44px);
        }
        .login-card {
          width: 480px; max-width: 100%; background: #fdfcf5; border-radius: 20px;
          padding: 48px 44px 40px;
          box-shadow: 0 25px 60px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.08);
          position: relative; overflow: hidden;
        }
        .login-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, #448696, #fed774, #fdac84);
        }
        .card-logo { display: flex; align-items: center; gap: 10px; margin-bottom: 28px; }
        .card-logo img { width: 44px; height: 44px; object-fit: contain; }
        .card-logo-name { font-size: 20px; font-weight: 800; letter-spacing: 1.5px; color: #1a1a1a; }
        .card-title { font-size: 26px; font-weight: 700; color: #1a1a1a; margin-bottom: 6px; }
        .card-subtitle { font-size: 14.5px; color: #6b7280; font-weight: 500; margin-bottom: 28px; }

        /* ─── Error ─── */
        .login-error { padding: 14px 16px; border-radius: 12px; border-left: 4px solid; margin-bottom: 20px; display: flex; align-items: flex-start; gap: 10px; }
        .login-error.error-blocked { background: #fef2f2; border-color: #ef4444; }
        .login-error.error-suspended { background: #fff7ed; border-color: #f97316; }
        .login-error.error-expired { background: #fefce8; border-color: #eab308; }
        .login-error.error-error { background: #fef2f2; border-color: #ef4444; }
        .login-error .error-icon { flex-shrink: 0; margin-top: 1px; }
        .login-error.error-blocked .error-icon { color: #dc2626; }
        .login-error.error-suspended .error-icon { color: #ea580c; }
        .login-error.error-expired .error-icon { color: #ca8a04; }
        .login-error.error-error .error-icon { color: #dc2626; }
        .error-text { font-size: 13px; font-weight: 600; line-height: 1.4; }
        .login-error.error-blocked .error-text { color: #991b1b; }
        .login-error.error-suspended .error-text { color: #9a3412; }
        .login-error.error-expired .error-text { color: #854d0e; }
        .login-error.error-error .error-text { color: #991b1b; }
        .error-support { font-size: 12px; color: #6b7280; margin-top: 6px; }
        .error-support a { color: #448696; font-weight: 600; text-decoration: underline; }

        /* ─── Fields ─── */
        .field-group { margin-bottom: 20px; }
        .field-input-wrapper {
          position: relative; border: 1.5px solid #e5e7eb; border-radius: 12px;
          background: rgba(255,255,255,0.6); transition: all 0.2s ease;
        }
        .field-input-wrapper:focus-within {
          border-color: #448696; background: #fdfcf5;
          box-shadow: 0 0 0 4px rgba(68,134,150,0.12);
        }
        .field-label {
          position: absolute; top: 10px; left: 16px;
          font-size: 11px; font-weight: 600; color: #6b7280;
          text-transform: uppercase; letter-spacing: 0.5px; pointer-events: none; z-index: 1;
        }
        .field-input {
          width: 100%; padding: 30px 48px 10px 16px; border: none; background: transparent;
          font-size: 15px; font-weight: 500; color: #1a1a1a; outline: none;
          font-family: 'DM Sans', sans-serif;
        }
        .field-input::placeholder { color: #9ca3af; }
        .field-actions { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); display: flex; align-items: center; gap: 6px; }
        .field-action-btn {
          width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
          border: none; background: transparent; color: #9ca3af; cursor: pointer; border-radius: 8px; transition: all 0.15s;
        }
        .field-action-btn:hover { color: #4b5563; background: rgba(0,0,0,0.05); }
        .forgot-link {
          display: inline-block; margin-top: 8px; font-size: 13px; font-weight: 600;
          color: #448696; text-decoration: none; text-align: right; width: 100%;
        }
        .forgot-link:hover { color: #367080; text-decoration: underline; }
        .totp-panel {
          margin: 0 0 20px;
          padding: 14px;
          border-radius: 12px;
          border: 1px solid rgba(68,134,150,0.22);
          background: rgba(68,134,150,0.08);
        }
        .totp-title { margin: 0 0 6px; font-size: 13px; font-weight: 800; color: #1a1a1a; }
        .totp-text { margin: 0 0 10px; font-size: 12px; line-height: 1.45; color: #4b5563; }
        .totp-setup-row {
          display: grid;
          grid-template-columns: 132px 1fr;
          gap: 12px;
          align-items: center;
          margin-bottom: 10px;
        }
        .totp-qr {
          width: 132px;
          height: 132px;
          padding: 6px;
          border-radius: 10px;
          background: #ffffff;
          border: 1px solid rgba(68,134,150,0.22);
        }
        .totp-secret {
          display: block;
          padding: 10px 12px;
          border-radius: 10px;
          background: #fdfcf5;
          color: #1a1a1a;
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: 13px;
          font-weight: 700;
          word-break: break-all;
          border: 1px dashed rgba(68,134,150,0.45);
        }

        /* ─── Submit ─── */
        .login-submit {
          width: 100%; padding: 16px 24px; border: none; border-radius: 12px;
          font-size: 15px; font-weight: 600; color: #fdfcf5; background: #448696;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px;
          transition: all 0.2s ease; margin-top: 24px; font-family: 'DM Sans', sans-serif;
        }
        .login-submit:hover:not(:disabled) { background: #367080; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(68,134,150,0.35); }
        .login-submit:disabled { opacity: 0.6; cursor: not-allowed; }
        .spinner { width: 18px; height: 18px; border: 2px solid rgba(253,252,245,0.3); border-top-color: #fdfcf5; border-radius: 50%; animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ─── Remember ─── */
        .remember-row { display: flex; align-items: center; gap: 10px; margin-top: 16px; }
        .remember-row input[type="checkbox"] { width: 16px; height: 16px; accent-color: #448696; cursor: pointer; }
        .remember-row label { font-size: 13px; color: #6b7280; font-weight: 500; cursor: pointer; }

        /* ─── Card Footer ─── */
        .card-footer { text-align: center; margin-top: 24px; padding-top: 20px; border-top: 1px solid #edecea; }
        .card-footer-help { font-size: 13px; color: #9ca3af; font-weight: 500; }
        .card-footer-help a { color: #448696; font-weight: 600; text-decoration: none; }
        .card-footer-help a:hover { text-decoration: underline; color: #367080; }


        /* ─── Bottom Bar ─── */
        .login-bottom-bar {
          padding: 10px 32px; background: #fed774;
          display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap;
        }

        .bottom-link { font-size: 12px; color: #1a1a1a; text-decoration: none; font-weight: 600; transition: color 0.15s; }
        .bottom-link:hover { color: #4b5563; text-decoration: underline; }
        .bottom-sep { color: rgba(0,0,0,0.2); font-size: 12px; }

        /* ─── Responsive ─── */
        @media (max-width: 1024px) {
          .login-page-main { flex-direction: column; }
          .login-branding { min-height: 50vh; padding: 24px 24px 80px; }
          .ad-featured { max-width: 100%; }
          .hero-headline { font-size: 32px; }
          .hero-container { min-height: 180px; }
          .brand-logo img { width: 64px; height: 64px; }
          .brand-logo-text { font-size: 36px; }
          .login-card-wrapper { min-height: auto; padding: 24px 20px 80px; }
          .login-card { width: 100%; max-width: 480px; }
        }

        @media (max-width: 480px) {
          /* ── Masquer ticker et branding sur mobile ── */
          .ad-ticker { display: none; }
          .login-branding { display: none; }

         /* ── Page principale — fond blanc ── */
          .login-page-main {
            padding-top: 0;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #fdfcf5;
          }

          /* ── Fond image masqué ── */
          .login-bg { display: none; }

        /* ── Card wrapper — centré plein écran ── */
        .login-card-wrapper {
          width: 100%;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px 20px 110px;
        }

          /* ── Card login ── */
         .login-card {
            width: 100%;
            max-width: 420px;
            padding: 36px 24px 28px;
            border-radius: 0;
            box-shadow: none;
            min-height: calc(100vh - 110px);
            display: flex;
            flex-direction: column;
          }

         .copyright-block {
              display: none !important;
            }

          /* ── Bottom bar ── */
         .login-bottom-bar {
            gap: 6px 10px;
            padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
            flex-wrap: wrap;
            justify-content: center;
          }
          .bottom-link { font-size: 11px; }
          .bottom-sep { display: inline !important; font-size: 10px; color: rgba(0,0,0,0.25); }
          .totp-setup-row { grid-template-columns: 1fr; }

          .copyright-text {
            color: #9ca3af !important;
          }
        }


        /* ─── Tablettes 11-13 pouces (768px - 1366px) ─── */
        @media screen and (min-width: 768px) and (max-width: 1366px) {
          .login-page-main,
          .ad-ticker {
            zoom: 0.88;
          }
          .ad-ticker {
            top: 0;
            width: calc(100% / 0.88);
          }
          .login-bottom-bar {
            zoom: 0.88;
            width: calc(100% / 0.88);
          }
        }

        /* ─── Tablettes plus grandes 13-15 pouces (1367px - 1600px) ─── */
        @media screen and (min-width: 1367px) and (max-width: 1600px) {
          .login-page-main,
          .ad-ticker {
            zoom: 0.94;
          }
          .ad-ticker {
            top: 0;
            width: calc(100% / 0.94);
          }
          .login-bottom-bar {
            zoom: 0.94;
            width: calc(100% / 0.94);
          }
        }


      `}</style>

      {/* ══════ TOP TICKER ══════ */}
      <div className="ad-ticker">
        <div className="ad-ticker-label">
          NOS PARTENAIRES
        </div>
        <div className="ad-ticker-track">
          <div className="ad-ticker-scroll">
            {tickerItems.map((ad, i) => (
              <a key={i} href={ad.url} target="_blank" rel="noopener noreferrer" className="ad-ticker-item">
                <span className="ticker-company">{ad.company}</span>
                <span className="ticker-sep">—</span>
                {ad.tagline}
                <span className="ticker-dot" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ══════ MAIN PAGE ══════ */}
      <div className="login-page-main">
        <div className="login-bg">
          <img src={backgroundImage} alt="" loading="eager" />
        </div>

        <div className="login-branding">
          {/* Featured Ad Card */}
          {currentAd && (
            <div className="ad-featured">
              <a href={currentAd.url} target="_blank" rel="noopener noreferrer" className="ad-card">
                <div className="ad-card-image-badge">
                  Annonces sponsorisées
                </div>

                <div className="ad-card-body">
                  <div className={`ad-card-animated ${isAdAnimating ? 'out' : 'in'}`}>
                    <div className="ad-card-header">
                      <span className="ad-card-company">{currentAd.company}</span>
                    </div>

                    <p className="ad-card-tagline">{currentAd.tagline}</p>
                    <div className="ad-card-footer">
                      <span className="ad-card-cta">
                        {currentAd.cta} <ExternalLink size={12} />
                      </span>
                      <div className="ad-card-dots">
                        {featuredAds.map((_, i) => (
                          <div key={i} className={`ad-card-dot ${i === currentAdIndex ? 'active' : 'inactive'}`} />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            </div>
          )}

          {/* Brand + Hero */}
          <div className="brand-section">
            <div className="hero-container">
              <div className={`hero-animated ${isAnimating ? 'fade-out' : 'fade-in'}`}>
                <h1 className="hero-headline">
                  {currentText.lines.map((line, index) => (
                    <React.Fragment key={index}>
                      <span style={{ color: currentText.color || '#fdfcf5' }}>{line}</span>
                      {index < currentText.lines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </h1>
                <p className="hero-subtitle">{currentText.subtitle}</p>
              </div>
            </div>
            <div className="hero-dots">
              {heroTexts.map((_, index) => (
                <div key={index} className={`hero-dot ${index === currentTextIndex ? 'active' : 'inactive'}`} />
              ))}
            </div>
          </div>
        </div>

        {/* Login Card */}
        <div className="login-card-wrapper">
          <div className="login-card">
            <div className="card-logo">
              <img src="/logo.png" alt="HoraPilot" />
              <span className="card-logo-name">HORAPILOT</span>
            </div>
            <h2 className="card-title">Connectez-vous</h2>
            <p className="card-subtitle">Accédez à votre espace de gestion</p>

            {displayError && (
              <div className={`login-error error-${errorType || 'error'}`}>
                <AlertTriangle size={18} className="error-icon" />
                <div>
                  <p className="error-text">{displayError}</p>
                  {(errorType === 'blocked' || errorType === 'suspended' || errorType === 'expired') && (
                    <p className="error-support">Contactez notre support : <a href="mailto:contact@danicedgroup.com">contact@danicedgroup.com</a></p>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="field-group">
                <div className="field-input-wrapper">
                  <label className="field-label">Adresse e-mail</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} className="field-input" placeholder="nom@entreprise.com" required autoComplete="email" />
                </div>
              </div>
              <div className="field-group">
                <div className="field-input-wrapper">
                  <label className="field-label">Mot de passe</label>
                  <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} className="field-input" placeholder="••••••••" required autoComplete="current-password" />
                  <div className="field-actions">
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="field-action-btn" tabIndex={-1}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <button type="button" onClick={() => setActivePopup('forgot')}
                  className="forgot-link"
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Mot de passe oublié ?
                </button>
              </div>

              {totpRequired && (
                <div className="totp-panel">
                  <p className="totp-title">Google Authenticator</p>
                  {(totpSetup?.secret || totpSetup?.qrCodeDataUrl) && (
                    <div>
                      <p className="totp-text">Scanne le QR code avec Google Authenticator, puis saisis le code genere.</p>
                      <div className="totp-setup-row">
                        {totpSetup?.qrCodeDataUrl && (
                          <img className="totp-qr" src={totpSetup.qrCodeDataUrl} alt="QR code Google Authenticator" />
                        )}
                        {totpSetup?.secret && (
                          <code className="totp-secret">{totpSetup.secret}</code>
                        )}
                      </div>
                    </div>
                  )}
                  {!totpSetup?.secret && !totpSetup?.qrCodeDataUrl && (
                    <p className="totp-text">Saisis le code a 6 chiffres de ton application Google Authenticator.</p>
                  )}
                  <div className="field-input-wrapper" style={{ marginTop: 12 }}>
                    <label className="field-label">Code OTP</label>
                    <input
                      type="text"
                      name="totpCode"
                      value={totpCode}
                      onChange={(event) => { setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                      className="field-input"
                      placeholder="000000"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                    />
                  </div>
                </div>
              )}

              <button type="submit" disabled={loading || contextLoading} className="login-submit">
                <span>{totpRequired ? 'Valider le code' : 'Se connecter'}</span>
              </button>

              <div className="remember-row">
                <input type="checkbox" id="remember" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                <label htmlFor="remember">Rester connecté</label>
              </div>
            </form>

           <div className="card-footer">
            <p className="card-footer-help">
              Besoin d'aide ?{' '}
              <button type="button" onClick={() => setActivePopup('aide')}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer',
                  color: '#448696', fontWeight: 600, fontFamily: 'inherit', fontSize: 'inherit',
                  textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                Contacter le support
              </button>
            </p>
          </div>
          </div>
        </div>


        {/* Bottom Bar — dynamique */}
      <div className="login-bottom-bar" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10 }}>
          {footerLinks.map((link, i) => {
            const isPopup = ['#aide', '#cgu', '#confidentialite'].includes(link.url);
            const popupKey = link.url?.replace('#', '');
            return (
              <React.Fragment key={i}>
                {i > 0 && <span className="bottom-sep">|</span>}
                {isPopup ? (
                  <button onClick={() => setActivePopup(popupKey)}
                    className="bottom-link"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
                    {link.label}
                  </button>
                ) : (
                  <a href={link.url}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className="bottom-link"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {link.label}
                    {link.external && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    )}
                  </a>
                )}
              </React.Fragment>
            );
          })}
        </div>

      {/* Copyright superposé — style SaaS */}

   <div style={{
      position: 'fixed', bottom: 80, left: 0, right: 0,
      display: 'flex', justifyContent: 'center', zIndex: 1,
      pointerEvents: 'none',
      padding: '0 16px',
      textAlign: 'center',
    }}
    className="copyright-block"
    >
      <span className="copyright-text" style={{
        fontSize: 10, fontWeight: 500,
        color: 'rgb(255,255,255)',
        letterSpacing: '0.3px',
      }}>
        © {new Date().getFullYear()} · {copyrightText}
      </span>
    </div>


      </div>

      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} onSubmit={handlePasswordChange} tempPassword={tempPassword} />
      )}

      <LoginPopup
      isOpen={activePopup === 'aide'}
      onClose={() => setActivePopup(null)}
      titre={popupAide.titre}
      contenu={popupAide.contenu}
      icon={<HeadphonesIcon size={20} color="#448696" />}
      accentColor="#448696"
    />
    <LoginPopup
      isOpen={activePopup === 'cgu'}
      onClose={() => setActivePopup(null)}
      titre={popupCgu.titre}
      contenu={popupCgu.contenu}
      icon={<FileText size={20} color="#448696" />}
      accentColor="#448696"
    />
    <LoginPopup
      isOpen={activePopup === 'confidentialite'}
      onClose={() => setActivePopup(null)}
      titre={popupConfidentialite.titre}
      contenu={popupConfidentialite.contenu}
      icon={<ShieldCheck size={20} color="#448696" />}
      accentColor="#448696"
    />

    <LoginPopup
      isOpen={activePopup === 'forgot'}
      onClose={() => setActivePopup(null)}
      titre="Mot de passe oublié ?"
      icon={<KeyRound size={20} color="#448696" />}
      accentColor="#448696"
      contenu={`Vous avez oublié votre mot de passe ? Voici la marche à suivre :

      SI VOUS N'ÊTES PAS ADMINISTRATEUR
      Contactez directement votre administrateur d'entreprise. C'est lui qui peut réinitialiser votre mot de passe depuis l'application HoraPilot.

      SI VOUS ÊTES ADMINISTRATEUR
      Contactez notre support HoraPilot. Nous vérifierons votre identité et réinitialiserons votre accès.

      CONTACT SUPPORT
      Contact WhatsApp Congo : +242 06 697 43 33
      Email : support@horapilot.com
      Du lundi au samedi, de 8h00 a 21h00 (heure de France, UTC+1).

      IMPORTANT
      Pour des raisons de sécurité, aucun mot de passe ne sera communiqué par téléphone sans vérification préalable de votre identité.`}
      />


      <FullscreenLoader
        visible={showLoader}
        duration={1800}
        message={`Bienvenue dans votre espace ${loaderCompany}`}
      />
    </>
  );
};

export default LoginRH;
